import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from './api.ts';
import { TZ, fmtDate } from './date.ts';

/** 서버사이드 그리드 상태 (URL 쿼리에 보존) */
export type FilterOp = 'contains' | 'eq' | 'in' | 'nin' | 'gte' | 'lte' | 'bool';
export interface GridFilter { col: string; op: FilterOp; value: string[] }
export interface GridState { page: number; pageSize: number; sort: { col: string; dir: 'asc' | 'desc' } | null; filters: GridFilter[]; q: string }
export interface GridPage<T> { rows: T[]; total: number; page: number; pageSize: number; options: Record<string, Array<{ value: string; label: string }>> }

export function encodeFilter(f: GridFilter) { return `${f.col}~${f.op}~${f.op === 'in' || f.op === 'nin' ? f.value.join('|') : f.value[0] ?? ''}`; }
export function decodeFilter(s: string): GridFilter | null { const [col, op, ...rest] = s.split('~'); if (!col || !op) return null; const v = rest.join('~'); return { col, op: op as FilterOp, value: op === 'in' || op === 'nin' ? v.split('|').filter(Boolean) : [v] }; }

export function useGridState(prefix = 'g', defaults: Partial<GridState> = {}) {
  const [sp, setSp] = useSearchParams();
  const k = (n: string) => `${prefix}.${n}`;
  const state: GridState = useMemo(() => ({
    page: Number(sp.get(k('page')) ?? defaults.page ?? 1),
    pageSize: Number(sp.get(k('size')) ?? defaults.pageSize ?? 50),
    sort: sp.get(k('sort')) ? { col: sp.get(k('sort'))!.replace(/^-/, ''), dir: sp.get(k('sort'))!.startsWith('-') ? 'desc' : 'asc' } : defaults.sort ?? null,
    filters: sp.getAll(k('f')).map(decodeFilter).filter(Boolean) as GridFilter[],
    q: sp.get(k('q')) ?? '',
  }), [sp]); // eslint-disable-line
  const set = useCallback((patch: Partial<GridState>) => {
    const next = { ...state, ...patch };
    if (patch.page === undefined) next.page = 1; // 정렬·필터·검색이 바뀌면 1페이지로
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      for (const key of Array.from(n.keys())) if (key.startsWith(prefix + '.')) n.delete(key);
      if (next.page !== 1) n.set(k('page'), String(next.page));
      if (next.pageSize !== (defaults.pageSize ?? 50)) n.set(k('size'), String(next.pageSize));
      if (next.sort) n.set(k('sort'), `${next.sort.dir === 'desc' ? '-' : ''}${next.sort.col}`);
      for (const f of next.filters) n.append(k('f'), encodeFilter(f));
      if (next.q) n.set(k('q'), next.q);
      return n;
    }, { replace: true });
  }, [state, setSp]); // eslint-disable-line
  return { state, set };
}

export function gridQueryString(s: GridState, extra: Record<string, string> = {}) {
  const p = new URLSearchParams(extra);
  p.set('page', String(s.page)); p.set('pageSize', String(s.pageSize)); p.set('tz', TZ);
  if (s.sort) p.set('sort', `${s.sort.dir === 'desc' ? '-' : ''}${s.sort.col}`);
  for (const f of s.filters) p.append('f', encodeFilter(f));
  if (s.q) p.set('q', s.q);
  return p.toString();
}

export function useGridQuery<T>(key: string, path: string, state: GridState, extra: Record<string, string> = {}) {
  const qs = gridQueryString(state, extra);
  return useQuery({ queryKey: [key, path, qs], queryFn: () => api<GridPage<T>>(`${path}?${qs}`), placeholderData: keepPreviousData });
}

/* ── 클라이언트 모드: 이미 받은 배열을 로컬에서 정렬·필터·검색·페이지 처리 (상세 화면의 부속 표) ── */
export type CellValue = string | number | boolean | null | undefined;
export interface ClientColumnSpec<T> {
  key: string;
  filter?: 'text' | 'select' | 'bool' | 'date' | 'none';
  /** 달력 날짜 컬럼(date 형, 'YYYY-MM-DDT00:00:00.000Z'): 시간대 변환 없이 날짜만 비교. 없으면 시각으로 보고 브라우저 시간대의 날짜로 비교 */
  day?: boolean;
  options?: Array<{ value: string; label: string }>;
  /** 정렬 기준값 (없으면 filterValue → row[key]) */
  sortValue?: (row: T) => CellValue;
  /** 필터·검색·선택지 수집 기준값 (없으면 row[key]) */
  filterValue?: (row: T) => CellValue;
  /** select 선택지 라벨 (없으면 값 그대로) */
  optionLabel?: (value: string) => string;
}

const rawValue = <T,>(row: T, col: ClientColumnSpec<T>): CellValue => (col.filterValue ? col.filterValue(row) : ((row as Record<string, unknown>)[col.key] as CellValue));
const str = (v: CellValue) => (v == null ? '' : String(v));
function compare(a: CellValue, b: CellValue) {
  if (a == null || a === '') return b == null || b === '' ? 0 : 1; // 빈 값은 뒤로
  if (b == null || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), 'ko', { numeric: true });
}
function matches(v: CellValue, f: GridFilter, day = false) {
  const s = str(v);
  const d = () => (day ? s.slice(0, 10) : fmtDate(s, ''));
  switch (f.op) {
    case 'contains': return s.toLowerCase().includes((f.value[0] ?? '').toLowerCase());
    case 'eq': return s === (f.value[0] ?? '');
    case 'in': return f.value.includes(s);
    case 'nin': return !f.value.includes(s);
    case 'bool': return s === (f.value[0] ?? '');
    case 'gte': return s !== '' && d() >= (f.value[0] ?? '');
    case 'lte': return s !== '' && d() <= (f.value[0] ?? '');
    default: return true;
  }
}

/** 배열 + 그리드 상태 → GridPage. pageSize 0 이면 페이지를 자르지 않는다. */
export function clientPage<T>(rows: T[], columns: ClientColumnSpec<T>[], state: GridState): GridPage<T> {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  let out = rows;
  if (state.q) { const q = state.q.toLowerCase(); out = out.filter((r) => columns.some((c) => str(rawValue(r, c)).toLowerCase().includes(q))); }
  for (const f of state.filters) { const c = byKey.get(f.col); if (c) out = out.filter((r) => matches(rawValue(r, c), f, c.day)); }
  if (state.sort) {
    const c = byKey.get(state.sort.col);
    if (c) { const dir = state.sort.dir === 'desc' ? -1 : 1; const sv = (r: T) => (c.sortValue ? c.sortValue(r) : rawValue(r, c)); out = [...out].sort((a, b) => compare(sv(a), sv(b)) * dir); }
  }
  const options: GridPage<T>['options'] = {};
  for (const c of columns) {
    if (c.filter === 'select' && !c.options) {
      const vals = [...new Set(rows.map((r) => str(rawValue(r, c))).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ko', { numeric: true }));
      options[c.key] = vals.map((v) => ({ value: v, label: c.optionLabel?.(v) ?? v }));
    }
  }
  const total = out.length;
  const page = state.pageSize > 0 ? out.slice((state.page - 1) * state.pageSize, state.page * state.pageSize) : out;
  return { rows: page, total, page: state.page, pageSize: state.pageSize, options };
}

/** 로컬 상태 클라이언트 그리드. URL 에 보존하려면 external 에 useGridState() 결과를 넘긴다. */
export function useClientGrid<T>(rows: T[] | undefined, columns: ClientColumnSpec<T>[], defaults: Partial<GridState> = {}, external?: { state: GridState; set: (patch: Partial<GridState>) => void }) {
  const [local, setLocal] = useState<GridState>({ page: 1, pageSize: 20, sort: null, filters: [], q: '', ...defaults });
  const setLocalPatch = useCallback((patch: Partial<GridState>) => setLocal((s) => ({ ...s, ...patch, page: patch.page ?? 1 })), []);
  const state = external?.state ?? local;
  const set = external?.set ?? setLocalPatch;
  const data = useMemo(() => clientPage(rows ?? [], columns, state), [rows, columns, state]);
  return { state, set, data };
}

// ───────────────────────── CR-062: 열 표시·숨김 (브라우저 localStorage, 그리드별) ─────────────────────────
/** 저장 형식 { v: 1, hidden: [열 키] }. 서버 동기화(후속 CR)로 옮길 수 있게 키·형식을 고정한다 */
const COLS_KEY = (id: string) => `prism.grid.cols.${id}`;
function readHidden(id: string): string[] { try { const raw = localStorage.getItem(COLS_KEY(id)); if (!raw) return []; const j = JSON.parse(raw) as { v?: number; hidden?: unknown }; return Array.isArray(j.hidden) ? j.hidden.filter((k): k is string => typeof k === 'string') : []; } catch { return []; } }
function writeHidden(id: string, hidden: string[]) { try { if (hidden.length === 0) localStorage.removeItem(COLS_KEY(id)); else localStorage.setItem(COLS_KEY(id), JSON.stringify({ v: 1, hidden })); } catch { /* 저장 불가 시 기본 구성 */ } }
/** 그리드 id 별 숨긴 열 키. 열 정의에 없는 키는 무시하고 다음 저장 때 정리한다 */
export function useColumnVisibility(id: string | undefined, keys: string[]) {
  const [hidden, setHiddenState] = useState<string[]>(() => (id ? readHidden(id) : []));
  useEffect(() => { setHiddenState(id ? readHidden(id) : []); }, [id]);
  const effective = useMemo(() => hidden.filter((k) => keys.includes(k)), [hidden, keys]);
  const setHidden = useCallback((next: string[]) => { const clean = next.filter((k) => keys.includes(k)); setHiddenState(clean); if (id) writeHidden(id, clean); }, [id, keys]);
  const toggle = useCallback((key: string) => setHidden(effective.includes(key) ? effective.filter((k) => k !== key) : [...effective, key]), [effective, setHidden]);
  return { hidden: effective, toggle, reset: () => setHidden([]) };
}
